import { HelpCircle, Info } from "@tamagui/lucide-icons";
import type React from "react";
import type { PropsWithChildren } from "react";
import type { FlexAlignType } from "react-native";
import {
  type GetThemeValueForKey,
  Popover,
  type PopoverProps,
  View,
  XStack,
  YStack,
} from "tamagui";
import { Body } from "./Body";
import { Label } from "./Label";

export type PopoverHelpProps = {
  title?: string;
  color?: GetThemeValueForKey<"color">;
  description: string;
  alignSelf?: "auto" | FlexAlignType;
  size?: string;
};

export const PopoverHelp: React.FC<
  PropsWithChildren<PopoverProps & PopoverHelpProps>
> = ({ children, title, color, description, size = "$smaller", ...props }) => {
  return (
    <Popover
      stayInFrame
      size="$size.medium"
      allowFlip
      placement="top"
      {...props}
    >
      <Popover.Trigger
        cursor="pointer"
        flexGrow={0}
        flex={0}
        onPress={(e) => {
          e.stopPropagation();
          return false;
        }}
        alignSelf="stretch"
      >
        <View alignSelf="stretch">
          <HelpCircle color={color} size={size} />
        </View>
      </Popover.Trigger>

      <Popover.Content
        onPress={(e) => {
          e.stopPropagation();
        }}
        borderWidth={1}
        borderColor="$divider"
        enterStyle={{ y: -10, opacity: 0 }}
        exitStyle={{ y: -10, opacity: 0 }}
        padding={0}
        margin={"$space.medium"}
        maxWidth={280}
        borderRadius={0}
        backgroundColor={"$backgroundBlock"}
        shadowColor={"#000"}
        shadowOpacity={0.15}
        shadowRadius={24}
        shadowOffset={{ height: 0, width: 0 }}
        animation={[
          "quick",
          {
            opacity: {
              overshootClamping: true,
            },
          },
        ]}
      >
        <Popover.Arrow
          backgroundColor={"$backgroundBlock"}
          borderWidth={1}
          borderColor="$divider"
        />
        <YStack gap="$space.medium" padding="$space.medium">
          <XStack gap="$space.small" alignItems="center">
            <Info />
            <Label>{title ?? "Help"}</Label>
          </XStack>
          <Body>{description}</Body>
        </YStack>
        <Popover.Close
          asChild
          onPress={(e) => {
            e.stopPropagation();
            return false;
          }}
        >
          <XStack
            borderTopWidth={1}
            borderColor={"$divider"}
            width="100%"
            alignItems="center"
            cursor="pointer"
          >
            <View padding="$space.medium" flex={1}>
              <Body textAlign="center">I got it!</Body>
            </View>
          </XStack>
        </Popover.Close>
      </Popover.Content>
    </Popover>
  );
};
